/**
 * themePresets.js - Theme Configuration Data
 * 
 * @description
 * Defines all available theme presets for user profiles.
 * Themes are tier-restricted to encourage upgrades.
 * 
 * @themeStructure
 * {
 *   id: string - Unique theme identifier
 *   name: string - Display name
 *   preview: string - Emoji/icon for preview
 *   tier: 'free' | 'pro' | 'max' - Minimum tier required
 *   colors: {
 *     background: string - Page background color
 *     text: string - Primary text color
 *     primary: string - Primary accent color
 *     secondary: string - Secondary accent color
 *     linkBg: string - Link button background
 *     linkText: string - Link button text color
 *     linkHover: string - Link button hover color
 *   }
 * }
 * 
 * @tierAccess
 * - Free: 2 themes (default, dark)
 * - Pro: 5 themes (+ ocean, sunset, forest)
 * - Max: 8 themes (+ neon, minimal, gradient)
 * 
 * @usage
 * ```js
 * // Get themes for user tier
 * const themes = getThemesForTier('pro');
 * 
 * // Get specific theme
 * const theme = getThemeById('ocean');
 * ```
 * 
 * @customization
 * - Pro users can override theme colors
 * - Max users can add custom CSS on top
 * 
 * @exports
 * - themePresets: Array of all theme objects
 * - getThemesForTier(tier): Filter themes by tier
 * - getThemeById(id): Get specific theme object
 */

// Theme presets available for different tiers
export const themePresets = {
  // Free tier themes (2 themes)
  free: [
    {
      id: 'default',
      name: 'Default',
      colors: {
        primary: '#8b5cf6',
        secondary: '#ec4899',
        background: '#ffffff',
        text: '#111827',
        buttonBg: '#8b5cf6',
        buttonText: '#ffffff',
        linkBg: '#ffffff',
        linkText: '#111827',
        linkHover: '#8b5cf6',
      },
      font: 'Inter',
      preview: '🎨',
    },
    {
      id: 'dark',
      name: 'Dark Mode',
      colors: {
        primary: '#2980b9',
        secondary: '#16a085',
        background: '#1a1b1c',
        text: '#ffffff',
        buttonBg: '#2980b9',
        buttonText: '#ffffff',
        linkBg: '#2f3132',
        linkText: '#ffffff',
        linkHover: '#2980b9',
      },
      font: 'Inter',
      preview: '🌙',
    },
  ],
  // Pro tier themes (5 themes + limited customization)
  pro: [
    {
      id: 'ocean',
      name: 'Ocean',
      colors: {
        primary: '#0891b2',
        secondary: '#06b6d4',
        background: '#f0f9ff',
        text: '#0c4a6e',
        buttonBg: '#0891b2',
        buttonText: '#ffffff',
        linkBg: '#ffffff',
        linkText: '#0c4a6e',
        linkHover: '#0891b2',
      },
      font: 'Poppins',
      preview: '🌊',
    },
    {
      id: 'sunset',
      name: 'Sunset',
      colors: {
        primary: '#f97316',
        secondary: '#fb923c',
        background: '#fff7ed',
        text: '#7c2d12',
        buttonBg: '#f97316',
        buttonText: '#ffffff',
        linkBg: '#ffffff',
        linkText: '#7c2d12',
        linkHover: '#f97316',
      },
      font: 'Raleway',
      preview: '🌅',
    },
    {
      id: 'forest',
      name: 'Forest',
      colors: {
        primary: '#16a34a',
        secondary: '#22c55e',
        background: '#f0fdf4',
        text: '#14532d',
        buttonBg: '#16a34a',
        buttonText: '#ffffff',
        linkBg: '#ffffff',
        linkText: '#14532d',
        linkHover: '#16a34a',
      },
      font: 'Roboto',
      preview: '🌲',
    },
  ],
  // Max tier gets all themes plus full customization
  max: [
    {
      id: 'minimal',
      name: 'Minimal',
      colors: {
        primary: '#000000',
        secondary: '#666666',
        background: '#ffffff',
        text: '#000000',
        buttonBg: '#000000',
        buttonText: '#ffffff',
        linkBg: '#f5f5f5',
        linkText: '#000000',
        linkHover: '#333333',
      },
      font: 'Helvetica',
      preview: '⚫',
    },
    {
      id: 'neon',
      name: 'Neon',
      colors: {
        primary: '#a855f7',
        secondary: '#ec4899',
        background: '#18181b',
        text: '#ffffff',
        buttonBg: 'linear-gradient(to right, #a855f7, #ec4899)',
        buttonText: '#ffffff',
        linkBg: '#27272a',
        linkText: '#ffffff',
        linkHover: '#a855f7',
      },
      font: 'Space Mono',
      preview: '💜',
    },
  ],
};

// Get themes available for a specific tier
export const getThemesForTier = (tier) => {
  switch (tier) {
    case 'free':
      return themePresets.free;
    case 'pro':
      return [...themePresets.free, ...themePresets.pro];
    case 'max':
      return [...themePresets.free, ...themePresets.pro, ...themePresets.max];
    default:
      return themePresets.free;
  }
};

// Export all themes in a flat array (for when all features are free)
export const THEME_PRESETS = [
  ...themePresets.free,
  ...themePresets.pro,
  ...themePresets.max
]; 